import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Briefcase, Plus, CheckCircle2, ArrowRight, ArrowLeft, Sparkles, Save, Rocket, Trash2 } from "lucide-react";
import { BRL } from "@/lib/format";
import { toast } from "sonner";

const STEPS = [
  { key: "estrutura", title: "Estrutura", desc: "Modelo de hierarquia" },
  { key: "cargo", title: "Cargo base", desc: "Função e cargo de referência" },
  { key: "salario", title: "Salário e progressão", desc: "Base e percentual entre níveis" },
  { key: "auxiliar", title: "Auxiliar e arredondamento", desc: "Valor manual e múltiplo" },
  { key: "preview", title: "Prévia e salvar", desc: "Revisão da tabela" },
];

export default function JobPlansPage() {
  const { companies, companyParam } = useCompany();
  const { hasRole } = useAuth();
  const canEdit = hasRole("rh_admin");
  const [structures, setStructures] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(baseForm(companyParam));
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  function baseForm(cid) {
    return {
      company_id: cid || "",
      funcao: "",
      estrutura: "com_master_especialista",
      cargo_base: "Pleno",
      salario_base: 3500,
      percentual: 15,
      auxiliar_salario: 2000,
      arredondamento: 10,
      ativo: true,
    };
  }

  useEffect(() => {
    api.get("/job-plans/structures").then(({ data }) => setStructures(data));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = companyParam ? { company_id: companyParam } : {};
      const { data } = await api.get("/job-plans", { params });
      setPlans(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyParam]);

  const openNew = () => {
    setForm(baseForm(companyParam || companies[0]?.id));
    setPreview(null);
    setStep(0);
    setOpen(true);
  };

  const canAdmin = hasRole("rh_admin") || hasRole("master");

  const publishRoles = async (p) => {
    try {
      const { data } = await api.post(`/job-plans/${p.id}/publish-roles`);
      if (data.total_created > 0) {
        toast.success(`${data.total_created} cargo(s) publicado(s) em "Cargos detalhados"`);
      } else {
        toast.info("Todos os cargos deste plano já foram publicados.");
      }
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const deactivatePlan = async (p) => {
    if (!window.confirm(`Desativar o plano "${p.funcao}"?\nEle ficará oculto mas pode ser reativado depois.`)) return;
    try {
      await api.delete(`/job-plans/${p.id}`);
      toast.success("Plano desativado");
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const hardDeletePlan = async (p) => {
    if (!window.confirm(`EXCLUIR DEFINITIVAMENTE o plano "${p.funcao}"?\n\nEsta ação remove também os cargos publicados a partir dele e NÃO pode ser desfeita.\n\nTem certeza?`)) return;
    try {
      await api.delete(`/job-plans/${p.id}`, { params: { hard: true } });
      toast.success("Plano e cargos vinculados excluídos");
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const currentStructure = structures.find((s) => s.key === form.estrutura);
  const availableLevels = (currentStructure?.niveis || []).filter((n) => n !== "Auxiliar");

  const computePreview = async () => {
    try {
      const { data } = await api.post("/job-plans/preview", {
        funcao: form.funcao, estrutura: form.estrutura,
        cargo_base: form.cargo_base, salario_base: Number(form.salario_base),
        percentual: Number(form.percentual),
        auxiliar_salario: Number(form.auxiliar_salario || 0),
        arredondamento: Number(form.arredondamento || 10),
      });
      setPreview(data);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    }
  };

  const next = async () => {
    if (step === 3) await computePreview();
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/job-plans", { ...form,
        salario_base: Number(form.salario_base),
        percentual: Number(form.percentual),
        auxiliar_salario: Number(form.auxiliar_salario || 0),
        arredondamento: Number(form.arredondamento || 10) });
      toast.success("Plano salvo com versionamento");
      setOpen(false);
      load();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="jobplans-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Cargos e Salários</div>
          <h1 className="font-display text-3xl font-semibold">Plano de Cargos</h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground">
            Gere automaticamente níveis e salários a partir do cargo base.
          </p>
        </div>
        {canEdit && (
          <Button onClick={openNew} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="plan-new-btn">
            <Plus className="h-4 w-4 mr-2" /> Novo plano
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {loading ? (
          [1, 2].map((i) => <Skeleton key={i} className="h-[240px]" />)
        ) : plans.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground col-span-full">
            Nenhum plano cadastrado.
          </div>
        ) : plans.map((p) => (
          <div key={p.id} className="rounded-lg border border-border bg-white p-5" data-testid={`plan-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-md bg-[#F9F4E7] text-[#8A6B2B]">
                  <Briefcase className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-display text-[16px] font-semibold">{p.funcao}</div>
                  <div className="text-[11.5px] text-muted-foreground">
                    Estrutura: {p.estrutura.replace(/_/g, " ")} · Base: {p.cargo_base} · {p.percentual}% entre níveis
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-[#F9F4E7] text-[#8A6B2B] border-[#E8D9AE]">v{p.versao}</Badge>
                <Badge variant="outline" className={p.ativo ? "bg-emerald-50 text-emerald-700 border-emerald-200" : ""}>
                  {p.ativo ? "Ativo" : "Inativo"}
                </Badge>
              </div>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="text-left text-muted-foreground uppercase text-[10.5px] tracking-wider">
                    <th className="py-1.5">Nível</th><th className="py-1.5 text-right">Salário</th><th className="py-1.5 text-right">Δ vs Base</th>
                  </tr>
                </thead>
                <tbody>
                  {p.niveis.map((n) => {
                    const diff = ((n.salario / p.salario_base - 1) * 100).toFixed(1);
                    return (
                      <tr key={n.nome} className="border-t border-border">
                        <td className="py-1.5 font-medium">{p.funcao} {n.nome}</td>
                        <td className="py-1.5 text-right font-mono">{BRL(n.salario)}</td>
                        <td className={`py-1.5 text-right font-mono ${n.salario > p.salario_base ? "text-emerald-600" : n.salario < p.salario_base ? "text-amber-700" : "text-muted-foreground"}`}>
                          {n.salario > p.salario_base ? "+" : ""}{diff}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {canAdmin && (
              <div className="mt-4 pt-3 border-t border-border flex items-center justify-end gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => publishRoles(p)} data-testid={`plan-publish-${p.id}`}
                        className="bg-[#F9F4E7] border-[#E8D9AE] text-[#8A6B2B] hover:bg-[#F5EDD4]">
                  <Rocket className="h-3.5 w-3.5 mr-1.5" /> Publicar cargos
                </Button>
                <Button size="sm" variant="outline" onClick={() => deactivatePlan(p)} data-testid={`plan-deact-${p.id}`}>
                  Desativar
                </Button>
                <Button size="sm" variant="outline" onClick={() => hardDeletePlan(p)}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`plan-delete-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Excluir plano
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Wizard */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Assistente · Plano de Cargos</DialogTitle>
          </DialogHeader>

          {/* Stepper */}
          <div className="flex items-center justify-between gap-1 py-2 overflow-x-auto">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex items-center gap-2 min-w-0" data-testid={`step-${i}`}>
                <div className={`rh-step-dot text-[13px] ${
                  i < step ? "bg-[#234935] text-white" :
                  i === step ? "bg-[#C5A059] text-[#1A231F]" : "bg-secondary text-muted-foreground"
                }`}>{i < step ? <CheckCircle2 className="h-4 w-4" /> : i + 1}</div>
                <div className="min-w-0 hidden md:block">
                  <div className="text-[12.5px] font-medium truncate">{s.title}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{s.desc}</div>
                </div>
                {i < STEPS.length - 1 && <div className="h-px w-6 md:w-10 bg-border shrink-0" />}
              </div>
            ))}
          </div>

          <div className="mt-4">
            {step === 0 && (
              <div className="space-y-4">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Empresa</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger data-testid="wiz-company"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Estrutura hierárquica</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                    {structures.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        data-testid={`wiz-structure-${s.key}`}
                        onClick={() => setForm({ ...form, estrutura: s.key, cargo_base: s.niveis.includes("Pleno") ? "Pleno" : s.niveis[0] })}
                        className={`rounded-md border p-3 text-left transition-all ${
                          form.estrutura === s.key ? "border-[#234935] bg-[#234935]/5 ring-1 ring-[#234935]/30" : "border-border hover:border-foreground/30"
                        }`}
                      >
                        <div className="font-display text-[14px] font-semibold">{s.label}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {s.niveis.map((n) => (
                            <span key={n} className="text-[10.5px] uppercase tracking-wider bg-secondary px-1.5 py-0.5 rounded">{n}</span>
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Função base</Label>
                  <Input value={form.funcao} onChange={(e) => setForm({ ...form, funcao: e.target.value })}
                    placeholder="Ex: Mecânico" data-testid="wiz-funcao" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Cargo base de referência</Label>
                  <Select value={form.cargo_base} onValueChange={(v) => setForm({ ...form, cargo_base: v })}>
                    <SelectTrigger data-testid="wiz-cargo-base"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {availableLevels.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    O cargo de referência será o ponto central do cálculo. Os demais níveis serão gerados automaticamente.
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Salário do cargo base (R$)</Label>
                  <Input type="number" step="10" value={form.salario_base}
                    onChange={(e) => setForm({ ...form, salario_base: e.target.value })} data-testid="wiz-sal-base" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Percentual entre níveis (%)</Label>
                  <Input type="number" step="1" value={form.percentual}
                    onChange={(e) => setForm({ ...form, percentual: e.target.value })} data-testid="wiz-percentual" />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Salário do Auxiliar (manual)</Label>
                  <Input type="number" step="10" value={form.auxiliar_salario}
                    onChange={(e) => setForm({ ...form, auxiliar_salario: e.target.value })} data-testid="wiz-aux" />
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Conforme regra: o Auxiliar é informado manualmente e não entra no cálculo automático.
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider">Arredondamento (múltiplo de R$)</Label>
                  <Select value={String(form.arredondamento)} onValueChange={(v) => setForm({ ...form, arredondamento: Number(v) })}>
                    <SelectTrigger data-testid="wiz-arred"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1, 5, 10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{`R$ ${n},00`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
                  <Sparkles className="h-4 w-4 text-[#C5A059]" />
                  Prévia calculada automaticamente
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nível</TableHead>
                      <TableHead className="text-right">Salário</TableHead>
                      <TableHead className="text-right">Δ vs Base</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(preview?.niveis || []).map((n) => {
                      const diff = ((n.salario / Number(form.salario_base) - 1) * 100).toFixed(1);
                      const isBase = n.nome === form.cargo_base;
                      return (
                        <TableRow key={n.nome}>
                          <TableCell className="font-medium">
                            {n.nome} {isBase && <Badge variant="outline" className="ml-2 bg-[#F9F4E7] text-[#8A6B2B] border-[#E8D9AE]">Base</Badge>}
                          </TableCell>
                          <TableCell className="text-right font-mono">{BRL(n.salario)}</TableCell>
                          <TableCell className={`text-right font-mono ${n.salario > Number(form.salario_base) ? "text-emerald-600" : n.salario < Number(form.salario_base) ? "text-amber-700" : "text-muted-foreground"}`}>
                            {n.salario > Number(form.salario_base) ? "+" : ""}{diff}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <div className="rounded-md bg-secondary/50 p-3 text-[12px] text-muted-foreground">
                  Ao salvar, a versão anterior (se existir) para esta função na mesma empresa será marcada como inativa, preservando o histórico.
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="wiz-prev">
              <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={next} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="wiz-next"
                disabled={(step === 1 && !form.funcao)}>
                Avançar <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={save} disabled={saving} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="wiz-save">
                <Save className="h-4 w-4 mr-2" /> {saving ? "Salvando..." : "Salvar plano"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
